import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { HelpMarkdown, HelpToc } from "../help/HelpMarkdown";
import { usePageMeta } from "../help/usePageMeta";
import { PAGE_SEO } from "../../marketing/content/pageMeta";
import { pagePath } from "../../marketing/content/routes";
import { useMarketingLang } from "../../marketing/hooks/useMarketingPage";
import { getLegalMarkdown, type LegalDocumentId } from "./documents";

export function LegalPage({ document }: { document: LegalDocumentId }) {
  const { t, i18n } = useTranslation();
  const lang = useMarketingLang();
  const markdown = getLegalMarkdown(document, i18n.language);
  const seo = PAGE_SEO[document][lang];
  const canonicalPath = pagePath(document, lang);
  const title = seo.title;
  const description = seo.description;

  usePageMeta({
    title,
    description,
    canonicalPath,
    language: lang,
    ogType: "article",
  });

  const titleLine = markdown.split("\n").find((line) => line.startsWith("# "));
  const heading = titleLine ? titleLine.replace(/^#\s+/, "").trim() : title;
  const body = titleLine ? markdown.replace(titleLine, "").trimStart() : markdown;

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
      <nav className="mb-8 flex flex-wrap gap-2 text-sm" aria-label={t("landing.footer.legal", "Légal")}>
        <Link
          to={pagePath("privacy", lang)}
          className={
            document === "privacy"
              ? "rounded-full bg-primary px-3 py-1.5 font-medium text-white"
              : "rounded-full bg-muted px-3 py-1.5 font-medium text-foreground hover:bg-muted"
          }
        >
          {t("landing.footer.privacy", "Politique de confidentialité")}
        </Link>
        <Link
          to={pagePath("terms", lang)}
          className={
            document === "terms"
              ? "rounded-full bg-primary px-3 py-1.5 font-medium text-white"
              : "rounded-full bg-muted px-3 py-1.5 font-medium text-foreground hover:bg-muted"
          }
        >
          {t("landing.footer.terms", "Conditions d'utilisation")}
        </Link>
      </nav>
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-12">
        <article>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">{heading}</h1>
          <div className="mt-8 lg:hidden">
            <HelpToc markdown={markdown} />
          </div>
          <div className="mt-8">
            <HelpMarkdown content={body} />
          </div>
        </article>
        <aside className="mt-10 hidden lg:mt-0 lg:block">
          <div className="sticky top-24">
            <HelpToc markdown={markdown} />
          </div>
        </aside>
      </div>
    </main>
  );
}
